import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { MagnifyingGlass, CheckCircle, Confetti, Sun } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = '/api';

function ToggleRow({ label, labelEs, value, onChange, yesTestId, noTestId }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-[#FAF8F5] border border-[#E8E0D0] rounded">
      <div className="flex-1">
        <Label className="font-body text-sm text-stone-700">{label}</Label>
        <p className="font-body text-xs italic text-stone-400">{labelEs}</p>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          data-testid={yesTestId}
          onClick={() => onChange(true)}
          className={`px-4 py-1.5 text-sm font-body rounded transition-colors ${
            value === true
              ? 'bg-[#C9B99A] text-white'
              : 'bg-white border border-stone-200 text-stone-600 hover:border-[#C9B99A]'
          }`}
        >
          Yes / Sí
        </button>
        <button
          type="button"
          data-testid={noTestId}
          onClick={() => onChange(false)}
          className={`px-4 py-1.5 text-sm font-body rounded transition-colors ${
            value === false
              ? 'bg-stone-500 text-white'
              : 'bg-white border border-stone-200 text-stone-600 hover:border-stone-400'
          }`}
        >
          No
        </button>
      </div>
    </div>
  );
}

export default function EventsRSVPPage() {
  const [step, setStep] = useState('lookup'); // lookup, form, no-rsvp, success
  const [isLoading, setIsLoading] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [guest, setGuest] = useState(null);
  const [rsvp, setRsvp] = useState(null);
  const [primaryWP, setPrimaryWP] = useState(null);
  const [primaryBD, setPrimaryBD] = useState(null);
  const [members, setMembers] = useState([]);

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      toast.error('Please enter your first and last name');
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/invited-guests/lookup`, {
        params: { first_name: firstName.trim(), last_name: lastName.trim() }
      });
      const existingRsvp = response.data.existing_rsvp;
      if (!existingRsvp) {
        setGuest(response.data);
        setStep('no-rsvp');
        return;
      }
      setGuest(response.data);
      setRsvp(existingRsvp);
      setPrimaryWP(typeof existingRsvp.attending_welcome_party === 'boolean' ? existingRsvp.attending_welcome_party : null);
      setPrimaryBD(typeof existingRsvp.attending_beach_day === 'boolean' ? existingRsvp.attending_beach_day : null);
      setMembers(
        (existingRsvp.plus_ones || [])
          .filter(po => po.attending !== false)
          .map(po => ({
            name: po.name,
            wp: typeof po.attending_welcome_party === 'boolean' ? po.attending_welcome_party : null,
            bd: typeof po.attending_beach_day === 'boolean' ? po.attending_beach_day : null
          }))
      );
      setStep('form');
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("We couldn't find your name on the guest list. Please check the spelling or contact Stefanie & Walter.");
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (primaryWP === null || primaryBD === null) {
      toast.error('Please answer both questions for yourself / Por favor responde ambas preguntas');
      return;
    }
    for (const m of members) {
      if (m.wp === null || m.bd === null) {
        toast.error(`Please answer both questions for ${m.name}`);
        return;
      }
    }
    setIsLoading(true);
    try {
      await axios.post(`${API}/event-rsvp`, {
        guest_id: guest.id,
        attending_welcome_party: primaryWP,
        attending_beach_day: primaryBD,
        plus_ones: members.map(m => ({
          name: m.name,
          attending_welcome_party: m.wp,
          attending_beach_day: m.bd
        }))
      });
      setStep('success');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const updateMember = (index, field, value) => {
    const next = [...members];
    next[index] = { ...next[index], [field]: value };
    setMembers(next);
  };

  return (
    <div data-testid="events-rsvp-page" className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="page-header bg-white border-b border-stone-200">
        <div className="max-w-4xl mx-auto px-4">
          <p className="font-body text-sm tracking-[0.2em] uppercase mb-4" style={{ color: '#B8A88A' }}>
            Two More Celebrations
          </p>
          <h1 className="page-title">Welcome Party & Beach Day</h1>
          <p className="page-subtitle">
            Let us know if you'll join us for these events
            <span className="block font-body text-sm italic text-stone-400 mt-1">
              Indícanos si nos acompañarás a estos eventos
            </span>
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-12 space-y-6">
        {/* Event Info */}
        {step !== 'success' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-[#E8E0D0] p-5 space-y-1">
              <div className="flex items-center gap-2">
                <Confetti size={20} className="text-[#C9A86C]" />
                <h3 className="font-display text-base text-stone-900">Welcome Party</h3>
              </div>
              <p className="font-body text-sm text-stone-600">Thursday, December 10 — San Salvador</p>
              <p className="font-body text-xs italic text-stone-400">Fiesta de bienvenida — Jueves 10 de diciembre</p>
            </div>
            <div className="bg-white border border-[#E8E0D0] p-5 space-y-1">
              <div className="flex items-center gap-2">
                <Sun size={20} className="text-[#C9A86C]" />
                <h3 className="font-display text-base text-stone-900">Beach Day & Overnight in El Tunco</h3>
              </div>
              <p className="font-body text-sm text-stone-600">Saturday, December 12 — buses depart from Cardedeu Suites, San Salvador</p>
              <p className="font-body text-xs italic text-stone-400">Día de playa y noche en El Tunco — Sábado 12 de diciembre</p>
            </div>
          </div>
        )}

        {/* Lookup Step */}
        {step === 'lookup' && (
          <form onSubmit={handleLookup} className="bg-white border border-stone-200 p-8 space-y-5">
            <div className="text-center space-y-1">
              <MagnifyingGlass size={28} className="mx-auto text-[#C9B99A]" />
              <h2 className="font-display text-xl text-stone-900">Find Your RSVP</h2>
              <p className="font-body text-sm text-stone-500">
                Enter your name as it appears on your invitation
                <span className="block text-xs italic text-stone-400">Ingresa tu nombre como aparece en tu invitación</span>
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="font-body text-sm">First Name <span className="italic text-stone-400 text-xs">/ Nombre</span></Label>
                <Input
                  data-testid="event-lookup-first-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                />
              </div>
              <div className="space-y-2">
                <Label className="font-body text-sm">Last Name <span className="italic text-stone-400 text-xs">/ Apellido</span></Label>
                <Input
                  data-testid="event-lookup-last-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name"
                />
              </div>
            </div>
            <Button
              type="submit"
              data-testid="event-lookup-submit"
              disabled={isLoading}
              className="w-full bg-[#C9B99A] hover:bg-[#B8A88A] text-white"
            >
              {isLoading ? 'Searching...' : 'Find My RSVP / Buscar'}
            </Button>
          </form>
        )}

        {/* No wedding RSVP yet */}
        {step === 'no-rsvp' && (
          <div data-testid="event-no-rsvp" className="bg-white border border-stone-200 p-8 text-center space-y-4">
            <h2 className="font-display text-xl text-stone-900">We couldn't find your wedding RSVP yet</h2>
            <p className="font-body text-sm text-stone-600">
              Please RSVP for the wedding first — the form includes the Welcome Party and Beach Day questions too.
            </p>
            <p className="font-body text-xs italic text-stone-400">
              Por favor confirma primero tu asistencia a la boda — el formulario incluye las preguntas de la fiesta de bienvenida y el día de playa.
            </p>
            <Button asChild className="bg-[#C9B99A] hover:bg-[#B8A88A] text-white">
              <Link to="/rsvp" data-testid="event-go-to-rsvp">Go to Wedding RSVP</Link>
            </Button>
            <button
              type="button"
              onClick={() => setStep('lookup')}
              className="block mx-auto font-body text-xs text-stone-400 hover:text-stone-600 underline"
            >
              Search a different name
            </button>
          </div>
        )}

        {/* Form Step */}
        {step === 'form' && rsvp && (
          <div className="space-y-5">
            {rsvp.attending === false && (
              <div className="bg-amber-50 border border-amber-200 p-4 font-body text-sm text-amber-700">
                Our records show you declined the wedding. If your plans have changed, please update your full RSVP{' '}
                <Link to="/rsvp" className="underline">here</Link>.
              </div>
            )}

            {/* Primary guest */}
            <div className="bg-white border border-stone-200 p-6 space-y-4" data-testid="event-primary-card">
              <h3 className="font-display text-lg text-stone-900 border-b border-stone-100 pb-2">
                {guest.first_name} {guest.last_name}
              </h3>
              <ToggleRow
                label="Welcome Party — Dec 10?"
                labelEs="¿Fiesta de bienvenida — 10 de diciembre?"
                value={primaryWP}
                onChange={setPrimaryWP}
                yesTestId="event-primary-wp-yes"
                noTestId="event-primary-wp-no"
              />
              <ToggleRow
                label="Beach Day in El Tunco — Sat, Dec 12?"
                labelEs="¿Día de playa en El Tunco — sábado 12 de diciembre?"
                value={primaryBD}
                onChange={setPrimaryBD}
                yesTestId="event-primary-bd-yes"
                noTestId="event-primary-bd-no"
              />
            </div>

            {/* Plus ones */}
            {members.map((member, index) => (
              <div key={index} className="bg-white border border-stone-200 p-6 space-y-4" data-testid={`event-member-card-${index}`}>
                <h3 className="font-display text-lg text-stone-900 border-b border-stone-100 pb-2">{member.name}</h3>
                <ToggleRow
                  label="Welcome Party — Dec 10?"
                  labelEs="¿Fiesta de bienvenida — 10 de diciembre?"
                  value={member.wp}
                  onChange={(v) => updateMember(index, 'wp', v)}
                  yesTestId={`event-member-wp-yes-${index}`}
                  noTestId={`event-member-wp-no-${index}`}
                />
                <ToggleRow
                  label="Beach Day in El Tunco — Sat, Dec 12?"
                  labelEs="¿Día de playa en El Tunco — sábado 12 de diciembre?"
                  value={member.bd}
                  onChange={(v) => updateMember(index, 'bd', v)}
                  yesTestId={`event-member-bd-yes-${index}`}
                  noTestId={`event-member-bd-no-${index}`}
                />
              </div>
            ))}

            <Button
              data-testid="event-submit-btn"
              onClick={handleSubmit}
              disabled={isLoading}
              className="w-full bg-[#C9B99A] hover:bg-[#B8A88A] text-white py-6 text-base"
            >
              {isLoading ? 'Saving...' : 'Save My Answers / Guardar'}
            </Button>
          </div>
        )}

        {/* Success */}
        {step === 'success' && (
          <div data-testid="event-success" className="bg-white border border-stone-200 p-10 text-center space-y-4">
            <CheckCircle size={48} weight="fill" className="mx-auto text-[#C9B99A]" />
            <h2 className="font-display text-2xl text-stone-900">Thank You!</h2>
            <p className="font-body text-stone-600">
              Your Welcome Party and Beach Day answers have been saved. We can't wait to celebrate with you!
            </p>
            <p className="font-body text-sm italic text-stone-400">
              Tus respuestas han sido guardadas. ¡No podemos esperar para celebrar contigo!
            </p>
            <Button asChild variant="outline" className="border-[#C9B99A] text-[#B8A88A]">
              <Link to="/">Back to Home / Volver al Inicio</Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
